import { Grid } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { useEffect, useRef, useState } from 'react'
import { onEditAction } from '../../core/audit'
import {
  auditColumn,
  CollectionTable,
  Column,
  deleteColumn,
  editColumn,
  FilterControl,
  shimColumn,
} from '../../core/collections'
import { Auto, NumberText, PastDateInline, Text } from '../../core/controls'
import { defaultDateTimeFormat, formatDate } from '../../core/date-time'
import { Expansion, ExpansionDetails, ExpansionSummary } from '../../core/expansion'
import { personName, personNameWithDegree, truncateSpan } from '../../core/format'
import { orange } from '../../core/theme'
import { TrackingCodeApi } from '../../core/tracking-code-api'
import { UndoRedo } from '../../core/undo-redo'
import { isAuditReviewWorkflow,isReviewWorkflow } from '../../core/workflow-api'

export function TrackingCodeForm(api: TrackingCodeApi) {
  const { controls } = useRef(api.form).current as any

  return (
    <Grid
      container
      direction="row"
      spacing={2}
      style={{
        width: '100%',
        flexShrink: 0,
        flexWrap: 'nowrap',
        paddingLeft: '12px',
        paddingTop: '6px',
        paddingBottom: '8px',
      }}
    >
      <Grid item style={{ width: '25%', minWidth: 100 }}>
        <Auto {...controls.code} />
      </Grid>
      <Grid item style={{ width: '25%', minWidth: 100 }}>
        <Auto {...controls.provider} />
      </Grid>
      <Grid item style={{ width: '55px' }}>
        <NumberText {...controls.quantity} />
      </Grid>
      <Grid item>
        <PastDateInline {...controls.startDateTime} />
      </Grid>
      <Grid item>
        <PastDateInline {...controls.endDateTime} />
      </Grid>
      <Grid item>
        <Text {...controls.notes} />
      </Grid>
    </Grid>
  )
}

export function TrackingCodeCollection(api: TrackingCodeApi) {
  const { list, context } = api
  const { auditTracking } = context.config

  const isReview = isAuditReviewWorkflow()
  const actions = isReview
    ? [
        auditColumn(
          onEditAction('Edit Tracking Code Audit Comments', () => list.actions.refresh(), false, auditTracking),
        ),
      ]
    : []

  const columns: Column[] = [
    {
      name: 'description',
      label: 'Description',
      style: { width: '30%' },
      component: (e) => truncateSpan(e.value.description, '80px'),
    },
    {
      name: 'startDateTime',
      label: 'Start Date',
      component: (e) => formatDate(e.value.startDateTime, defaultDateTimeFormat),
      style: { width: '138px' },
    },
    {
      name: 'endDateTime',
      label: 'End Date',
      component: (e) => formatDate(e.value.endDateTime, defaultDateTimeFormat),
      style: { width: '138px' },
    },
    {
      name: 'provider',
      label: 'Physician',
      component: (e) => truncateSpan(personNameWithDegree(e.value.provider), '80px'),
    },
    {
      name: 'notes',
      label: 'Notes',
      component: (e) => truncateSpan(e.value.notes, '80px'),
    },
    ...actions,
    editColumn((e) => api.editItem(e.value)),
    deleteColumn((e) => api.removeItem(e.value)),
    shimColumn(),
  ]

  const values = list.data((s) => s.values)
  const [expanded, setExpanded] = useState(values.length > 0)
  const [clicked, setClicked] = useState(false)

  useEffect(() => {
    if (!clicked && !expanded) {
      setExpanded(values.length > 0)
    }
  })

  const onClicked = () => {
    setExpanded(!expanded)
    setClicked(true)
  }

  return (
    <Expansion expanded={expanded}>
      <ExpansionSummary onClick={onClicked} expandIcon={<ExpandMoreIcon id="tracking_code_expend_more" />}>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'row',
            gap: 24,
            alignItems: 'center',
            flexWrap: 'nowrap',
          }}
        >
          <Typography variant="subtitle2">{`TRACKING ${values.length ? '- ' + values.length : ''}`}</Typography>
          <div style={{ display: 'flex', flex: 1, flexGrow: 1 }} />

          <div onClick={(e) => e.stopPropagation()}>
            <UndoRedo api={api.listChanges} htmlColor={orange} label="tracking-code" />
          </div>

          <FilterControl api={list} />
        </div>
      </ExpansionSummary>
      <ExpansionDetails>
        <CollectionTable
          readonly={api.context.readonly}
          columns={columns}
          api={list}
          containerProps={{ elevation: 0 }}
          name="tracking-code-table"
          label={`TRACKING ${values.length ? '- ' + values.length : ''}`}
        />
      </ExpansionDetails>
    </Expansion>
  )
}
